/* ----------------------------------------------------------------------
 * Hoofdmenu op desktop — Headsprung, 9-10-2026
 *
 * Herschreven van hover naar klik. Wat er eerst gebeurde:
 *
 *   - een hoofditem opende zijn paneel op mouseenter;
 *   - over het scherm lag een vanglaag (.full-menu--cc) die het menu sloot
 *     op mousemove. Eén pixel naast het paneel en alles klapte dicht.
 *
 * Nu: je opent met een klik en het paneel blijft staan tot je het zelf
 * sluit — met het kruisje, met Escape, met een klik buiten het paneel, of
 * door nog eens op hetzelfde item te klikken.
 *
 * Een hoofditem met een submenu navigeert niet meer; je gaat naar de
 * collectie via "All Styles" of het eerste item in de lijst. Items zonder
 * submenu (Sale, Membership) blijven gewoon gewone links.
 *
 * De breedte van het paneel wordt gemeten aan de kolommen zelf, zodat de
 * kolommen in de CSS verschillende breedtes mogen hebben.
 * -------------------------------------------------------------------- */

if (!customElements.get('full-menu')) {
	class FullMenu extends HTMLElement {
		constructor() {
			super();

			this.header = this.closest('.header');
			this.ouders = this.querySelectorAll('.full-menu--link-parent');
			this.kinderen = this.querySelectorAll('.full-menu--link-child');
			this.panel = this.querySelector('.full-menu--panel');
			this.niveau1 = this.querySelector('.full-menu--panel--level-1');
			this.niveau2 = this.querySelector('.full-menu--panel--level-2');
			this.kolommen = this.querySelectorAll('.full-menu--panel--level');
			this.cc = this.querySelector('.full-menu--cc');
			this.toggle = this.querySelector('.full-menu--toggle');
			this.sluitknop = this.querySelector('.full-menu--close');
			this.standaardBreedte = 320;
			this.open = false;
			this.actieveOuder = null;
		}

		connectedCallback() {
			this.ouders.forEach((knop) => {
				knop.addEventListener('click', (e) => this.opOuderKlik(knop, e));
			});

			this.kinderen.forEach((knop) => {
				knop.addEventListener('click', (e) => this.opKindKlik(knop, e));
			});

			if (this.cc) this.cc.addEventListener('click', () => this.sluit());

			// De zoeklade van het thema schuift over de pagina; het menu hoort
			// dan dicht te gaan. Het openen zelf regelt predictive-search.js.
			const zoek = this.querySelector('.full-menu--search');
			if (zoek) zoek.addEventListener('click', () => this.sluit());
			if (this.toggle) this.toggle.addEventListener('click', (e) => this.opToggle(e));
			if (this.sluitknop) this.sluitknop.addEventListener('click', () => this.sluit(true));

			document.addEventListener('keyup', (e) => {
				if (e.code && e.code.toUpperCase() === 'ESCAPE' && this.open) this.sluit(true);
			});

			this.panel.style.setProperty('--menu-panel-width-total', this.breedteVoor(this.kolommen.length) + 'px');
			this.zetBreedte(1);
			this.meetBovenlijn();
			window.addEventListener('resize', () => this.meetBovenlijn());
		}

		/* De zichtbare breedte is de som van de kolommen tot en met het niveau
		 * dat open staat. Staat de grid nog niet opgebouwd, dan vallen we terug
		 * op de oude vaste 320 px per kolom. */
		breedteVoor(niveaus) {
			let breedte = 0;
			for (let i = 0; i < niveaus && i < this.kolommen.length; i++) {
				breedte += this.kolommen[i].offsetWidth || this.standaardBreedte;
			}
			return breedte || this.standaardBreedte * niveaus;
		}

		zetBreedte(niveaus) {
			this.panel.style.setProperty('--menu-panel-width', this.breedteVoor(niveaus) + 'px');
		}

		/* De drie kolommen moeten op dezelfde regel beginnen als het eerste
		 * navigatie-item. Dat item zit in de header en schuift mee met de
		 * aankondigingsbalk, terwijl het paneel vast aan de bovenkant hangt.
		 * Daarom meten we de hoogte en geven we die door aan de CSS. */
		meetBovenlijn() {
			const eerste = this.querySelector('.full-menu--menu-primary a');
			if (!eerste) return;
			const top = Math.round(eerste.getBoundingClientRect().top);
			if (top > 0) this.panel.style.setProperty('--menu-top', top + 'px');
		}

		/* Bij een hoofditem hoort soms een promotiebeeld; dat staat als los
		 * paneel in de derde kolom. Is het er, dan gaat het paneel meteen drie
		 * kolommen breed open. */
		promoVoor(knop) {
			if (!knop.dataset.target) return null;
			return this.niveau2.querySelector(knop.dataset.target + '-promo');
		}

		toonPromo(knop) {
			const promo = this.promoVoor(knop);
			if (!promo) return false;
			promo.classList.add('active');
			return true;
		}

		opent() {
			this.open = true;
			this.meetBovenlijn();
			this.header.classList.add('open-menu');
		}

		sluit(terugNaarKnop) {
			this.open = false;
			this.header.classList.remove('open-menu');
			this.zetBreedte(1);

			this.querySelectorAll('.full-menu--subpanel.active').forEach((el) => el.classList.remove('active'));
			this.ouders.forEach((el) => {
				el.classList.remove('is-open');
				if (el.classList.contains('has-sub-menu')) el.setAttribute('aria-expanded', 'false');
			});
			this.kinderen.forEach((el) => {
				el.classList.remove('is-open');
				if (el.classList.contains('has-sub-menu')) el.setAttribute('aria-expanded', 'false');
			});

			// Na Escape of het kruisje hoort de aandacht terug naar het item
			// waar je vandaan kwam, anders staat een toetsenbordgebruiker
			// ineens bovenaan de pagina.
			if (terugNaarKnop && this.actieveOuder) this.actieveOuder.focus();
			this.actieveOuder = null;
		}

		opOuderKlik(knop, e) {
			// Zonder submenu is het gewoon een link: laten gaan.
			if (!knop.classList.contains('has-sub-menu')) return;

			e.preventDefault();

			if (knop.classList.contains('is-open')) {
				this.sluit(true);
				return;
			}

			const doel = knop.dataset.target ? this.niveau1.querySelector(knop.dataset.target) : null;
			if (!doel) return;

			this.opent();
			this.actieveOuder = knop;

			this.ouders.forEach((el) => {
				el.classList.remove('is-open');
				if (el.classList.contains('has-sub-menu')) el.setAttribute('aria-expanded', 'false');
			});
			knop.classList.add('is-open');
			knop.setAttribute('aria-expanded', 'true');

			this.niveau1.querySelectorAll('.full-menu--subpanel').forEach((el) => el.classList.remove('active'));
			this.niveau2.querySelectorAll('.full-menu--subpanel').forEach((el) => el.classList.remove('active'));
			this.kinderen.forEach((el) => {
				el.classList.remove('is-open');
				if (el.classList.contains('has-sub-menu')) el.setAttribute('aria-expanded', 'false');
			});

			doel.classList.add('active');
			this.zetBreedte(this.toonPromo(knop) ? 3 : 2);
			window.dispatchEvent(new Event('resize'));
		}

		opKindKlik(knop, e) {
			if (!knop.classList.contains('has-sub-menu')) return;

			e.preventDefault();

			if (knop.classList.contains('is-open')) {
				knop.classList.remove('is-open');
				knop.setAttribute('aria-expanded', 'false');
				this.niveau2.querySelectorAll('.full-menu--subpanel').forEach((el) => el.classList.remove('active'));
				// Het promotiebeeld van het hoofditem neemt de kolom weer over.
				const terug = this.actieveOuder && this.toonPromo(this.actieveOuder);
				this.zetBreedte(terug ? 3 : 2);
				return;
			}

			const doel = knop.dataset.target ? this.niveau2.querySelector(knop.dataset.target) : null;
			if (!doel) return;

			this.kinderen.forEach((el) => {
				el.classList.remove('is-open');
				if (el.classList.contains('has-sub-menu')) el.setAttribute('aria-expanded', 'false');
			});
			knop.classList.add('is-open');
			knop.setAttribute('aria-expanded', 'true');

			this.niveau2.querySelectorAll('.full-menu--subpanel').forEach((el) => el.classList.remove('active'));
			doel.classList.add('active');
			this.zetBreedte(3);
			window.dispatchEvent(new Event('resize'));
		}

		/* De hamburger verschijnt zodra de header meescrollt. Die opent nu niet
		 * alleen, maar sluit ook weer. */
		opToggle(e) {
			e.preventDefault();
			if (this.open) {
				this.sluit();
			} else {
				this.opent();
			}
		}
	}

	customElements.define('full-menu', FullMenu);
}
